import { useQuery } from "@tanstack/react-query";
import {
  DropdownInput,
  Pill,
  type ControlledDropdownInputProps,
} from "@validereinc/common-components";
import { UserTypeEnumType, UsersAdapter } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useMemo } from "react";

export const UsersDropdownInput = ({
  excludeUserIds,
  name,
  isMulti,
  value,
  showUserStatus = true,
  typeFilter,
  ...restProps
}: ControlledDropdownInputProps & {
  value?: string | string[];
  excludeUserIds?: string[];
  showUserStatus?: boolean;
  typeFilter?: UserTypeEnumType[];
}) => {
  const singleQueryPayload: Parameters<typeof UsersAdapter.getList>[0] = {
    filters: {
      id: (value ?? isMulti) ? [] : "",
      ...(typeFilter ? { type: typeFilter } : {}),
    },
  };

  const defaultValueQuery = useQuery({
    queryKey: ["users", singleQueryPayload],
    queryFn: async () => {
      const { data } = await UsersAdapter.getList(singleQueryPayload);

      return data?.[0];
    },
    enabled: Boolean(value),
  });

  const allUsersQueryPayload: Parameters<typeof UsersAdapter.getList>[0] = {
    filters: { ...(typeFilter ? { type: typeFilter } : {}) },
  };

  const allUsersQuery = useQuery({
    queryKey: ["users", allUsersQueryPayload],
    queryFn: async () => {
      return UsersAdapter.getList(allUsersQueryPayload);
    },
  });

  const opts = useMemo(
    () =>
      allUsersQuery.data?.data.map((user) => ({
        name: user.name,
        id: user.id,
        slotRight: showUserStatus ? (
          <Pill variant={user.status === "active" ? "success" : "default"}>
            {toTitleCaseString(user.status, { intent: "ui" })}
          </Pill>
        ) : undefined,
      })) ?? [],
    [allUsersQuery.data?.data, showUserStatus]
  );

  return (
    <DropdownInput
      name={name ?? "user"}
      placeholder="Select a user"
      isMulti={isMulti}
      {...restProps}
      options={opts}
      // @ts-expect-error TODO: defaultValue has unknown in the union
      defaultValue={defaultValueQuery.data ?? ""}
      isLoading={allUsersQuery.isLoading}
      labelKey="name"
      valueKey="id"
      onFetchData={async (payload) => {
        let { data } = await UsersAdapter.getList({
          ...payload,
          filters: {
            name: payload.searchTerm,
            ...(typeFilter ? { type: typeFilter } : {}),
          },
        });

        // REVIEW: not a fan of the fact that we need to do this. A refactor of DropdownInput is needed.
        if (Array.isArray(payload.value)) {
          data = data.filter((d) => payload.value.includes(d.id));
        }

        if (excludeUserIds?.length) {
          data = data.filter((u) => !excludeUserIds.includes(u.id));
        }

        return data.map((user) => ({
          name: user.name,
          id: user.id,
          slotRight: showUserStatus ? (
            <Pill variant={user.status === "active" ? "success" : "default"}>
              {toTitleCaseString(user.status, { intent: "ui" })}
            </Pill>
          ) : undefined,
        }));
      }}
    />
  );
};
